/**
 * Atlas PM · 项目管理 · 数据 hooks（TanStack Query）
 *
 * 缓存失效统一走 api/queryKeys 的工厂，保证与地图 / 星图口径一致。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { qk } from '../../api/queryKeys';
import { loadDivisions } from '../../data/china-divisions';
import {
  loadProvinces,
  projectApi,
  type ProjectInput,
  type ProjectListQuery,
  type ProjectUpdateInput,
} from './api';

export function useProjectList(query: ProjectListQuery) {
  return useQuery({
    queryKey: qk.projects(query),
    queryFn: () => projectApi.list(query),
    placeholderData: (prev) => prev,
  });
}

export function useProjectDetail(id: string | undefined) {
  return useQuery({
    queryKey: qk.project(id ?? ''),
    queryFn: () => projectApi.detail(id as string),
    enabled: Boolean(id),
  });
}

/** 34 个省级行政区（静态资源，永久缓存） */
export function useProvinces() {
  return useQuery({
    queryKey: qk.provinces(),
    queryFn: loadProvinces,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/**
 * ⭐ P6.65 全国省 / 市 / 区县三级行政区划（含中心点坐标）。
 * 数据走动态 import 分包，仅打开项目表单时加载；永久缓存。
 */
export function useDivisions() {
  return useQuery({
    queryKey: ['china-divisions'],
    queryFn: loadDivisions,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/** 写操作后失效项目相关缓存（列表 / 地图 / 统计 / 详情） */
function useProjectInvalidate() {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['projects'] }),
      qc.invalidateQueries({ queryKey: qk.projectMap() }),
      qc.invalidateQueries({ queryKey: qk.projectStats() }),
    ]);
  };
}

export function useCreateProject() {
  const invalidate = useProjectInvalidate();
  return useMutation({
    mutationFn: (input: ProjectInput) => projectApi.create(input),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateProject(id: string) {
  const invalidate = useProjectInvalidate();
  return useMutation({
    mutationFn: (input: ProjectUpdateInput) => projectApi.update(id, input),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteProject() {
  const invalidate = useProjectInvalidate();
  return useMutation({
    mutationFn: (id: string) => projectApi.remove(id),
    onSuccess: () => invalidate(),
  });
}
